import React, { useState } from 'react'
import { css } from '@emotion/core'
import { colors, Icon, Typography } from '@dw/helix'

interface PaginationProps {
  total: number
  currentPage?: number
  perPage?: number
  onChange?: (currentPage: number) => any
  className?: string
}

const paginationStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 33px;
`

const buttonStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  background-color: transparent;
  border: 0;
  height: 35px;
  width: 30px;
  color: ${colors.brandBlue};

  &:disabled {
    color: ${colors.grey3};
  }

  svg {
    width: 10px;
    fill: currentColor;
  }
`

export const calculateLastPage = (total: number, perPage: number) => Math.ceil(total / perPage)

const setInitialPage = ({ total, currentPage, perPage }: { total: number; currentPage: number; perPage: number }) => {
  if (currentPage < calculateLastPage(total, perPage) && currentPage >= 1) {
    return currentPage
  }
  return 1
}

const Pagination: React.FunctionComponent<PaginationProps> = ({
  total,
  currentPage = 1,
  perPage = 20,
  onChange = () => {},
  className,
  ...props
}) => {
  const [page, setPage] = useState(
    setInitialPage({
      total,
      currentPage,
      perPage,
    }),
  )
  const lastPage = calculateLastPage(total, perPage)
  const back = () => {
    const nextPage = page - 1 < 1 ? 1 : page - 1
    setPage(nextPage)
    if (typeof onChange === 'function') {
      onChange(nextPage)
    }
  }
  const forward = () => {
    const nextPage = page + 1 > lastPage ? page : page + 1
    setPage(nextPage)
    if (typeof onChange === 'function') {
      onChange(nextPage)
    }
  }
  return (
    <div css={paginationStyles} className={className}>
      <button onClick={back} disabled={page === 1} type="button" css={buttonStyles}>
        <Icon name="chevron-left" />
      </button>
      <Typography
        type="snippet"
        css={css`
          padding: 0 35px;
        `}
      >
        {page} / {lastPage}
      </Typography>
      <button onClick={forward} disabled={page === lastPage} type="button" css={buttonStyles}>
        <Icon name="chevron-right" />
      </button>
    </div>
  )
}

export default Pagination
